import type { ReactElement } from "react";

import styled from "styled-components";

import { SWReactCountryIcons, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, mixins } from "@similarweb/styles";
import { Combobox } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICountryObject } from "app/services/CountryService";

// Legacy CountryCodeIcon (US-state code circle) is not exported from ui-components, so the badge is
// recreated here for `parent`-bearing items (states).
const StateCodeBadge = styled.span`
    ${mixins.setFontVariant("caption")};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 10px;
    color: ${colorsPalettes.carbon[400]};
    background-color: ${colorsPalettes.carbon[50]};
`;

type CountryOptionItemProps = {
    country: ICountryObject;
    isSelected: boolean;
    multi: boolean;
};

/**
 * Default option content for a country row inside the Combobox: flag (or state-code badge for
 * `parent`-bearing items), name and the "combined" device glyph. Multi mode renders a selection
 * checkbox; non-permitted countries render a lock instead (their click is intercepted via
 * `onRestrictedClick`). Presentational only — row chrome (padding, height, hover band) and the click
 * are owned by the wrapping Combobox.Option.
 */
export const CountryOptionItem = ({ country, isSelected, multi }: CountryOptionItemProps): ReactElement => {
    const showDeviceGlyph = Boolean(country.showDeviceIcon);
    const isRestricted = country.permitted === false;

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            width="100%"
        >
            <Flex
                alignItems="center"
                gap="8px"
                flex="1"
                minWidth={0}
            >
                {country.parent ? (
                    <StateCodeBadge>{country.code}</StateCodeBadge>
                ) : (
                    <SWReactCountryIcons
                        countryCode={country.id}
                        size="sm"
                    />
                )}
                <Combobox.OptionLabel>{country.text}</Combobox.OptionLabel>
                {showDeviceGlyph ? (
                    <SWReactIcons
                        iconName="combined"
                        size="xs"
                    />
                ) : null}
            </Flex>
            {isRestricted ? (
                <SWReactIcons
                    iconName="locked"
                    size="xs"
                />
            ) : multi ? (
                <Combobox.OptionCheckbox isChecked={isSelected} />
            ) : null}
        </Flex>
    );
};
